import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Building2, Mail, Lock, User,
  CheckCircle, XCircle, Loader2, AlertCircle,
  Eye, EyeOff, ShieldCheck, LogIn,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { orgTeamService } from '../services/orgTeamService';
import toast from 'react-hot-toast';

const isDev = process.env.NODE_ENV === 'development';
const devLog = (...args) => isDev && console.log(...args);

const OrgAcceptInvitation = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { currentUser: user, loading: authLoading, refreshCustomClaims } = useAuth();

  const [status, setStatus] = useState('loading');
  const [invitation, setInvitation] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ name: '', password: '', confirmPassword: '' });
  const [errors, setErrors] = useState({});

  const tokenCacheRef = useRef({ token: null, result: null });

  useEffect(() => {
    if (!token) { setStatus('invalid'); return; }

    const verify = async () => {
      if (tokenCacheRef.current.token === token && tokenCacheRef.current.result) {
        setInvitation(tokenCacheRef.current.result);
        setStatus('valid');
        return;
      }
      try {
        const result = await orgTeamService.verifyInvitationToken(token);
        if (!result || !result.valid) {
          setStatus(result?.reason === 'expired' ? 'expired' : 'invalid');
          return;
        }
        const data = { token, ...result.invitation };
        tokenCacheRef.current = { token, result: data };
        setInvitation(data);
        setStatus('valid');
      } catch (error) {
        devLog('Error verifying org token:', error);
        setStatus('error');
      }
    };
    verify();
  }, [token]);

  const validateForm = () => {
    const newErrors = {};
    if (!user && !invitation?.accountExists) {
      if (!formData.name.trim()) newErrors.name = 'El nombre es requerido';
      else if (formData.name.trim().length < 2) newErrors.name = 'El nombre debe tener al menos 2 caracteres';
      if (!formData.password) newErrors.password = 'La contraseña es requerida';
      else if (formData.password.length < 6) newErrors.password = 'La contraseña debe tener al menos 6 caracteres';
      if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = 'Las contraseñas no coinciden';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAccept = async (e) => {
    if (e) e.preventDefault();
    if (!validateForm()) return;
    setIsProcessing(true);
    try {
      const result = await orgTeamService.acceptInvitation(
        token,
        user ? (user.displayName || user.email) : formData.name,
        user ? null : formData.password
      );
      if (result.success) {
        setStatus('accepted');
        toast.success('¡Te has unido a la cadena!');
        if (user) await refreshCustomClaims();
        setTimeout(() => navigate('/chain'), 2000);
      }
    } catch (error) {
      devLog('Error accepting org invitation:', error);
      const msg = error.message || 'Error al aceptar la invitación';
      if (msg.includes('expirado') || msg.includes('expired')) setStatus('expired');
      else if (msg.includes('no encontrada') || msg.includes('not found')) setStatus('invalid');
      else toast.error(msg.replace('Firebase: ', '').replace(/\s*\(.*\)$/, ''));
    } finally {
      setIsProcessing(false);
    }
  };

  const maskEmail = (email) => {
    if (!email) return '';
    const [local, domain] = email.split('@');
    if (local.length <= 3) return `${local[0]}***@${domain}`;
    return `${local.slice(0, 3)}***@${domain}`;
  };

  // --- Estados de pantalla ---
  if (authLoading || status === 'loading') {
    return (
      <div className="min-h-screen bg-surface-app flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={48} className="animate-spin text-kumia-green mx-auto mb-4" />
          <p className="text-content-muted">Verificando invitación...</p>
        </div>
      </div>
    );
  }

  if (status === 'invalid' || status === 'expired' || status === 'error') {
    const cfg = {
      invalid: { icon: XCircle, border: 'border-red-500/30', iconBg: 'bg-red-50 dark:bg-red-900/20', iconColor: 'text-red-700 dark:text-red-400', title: 'Invitación No Válida', msg: 'Esta invitación no existe o ya fue utilizada. Solicita una nueva al dueño de la cadena.' },
      expired: { icon: AlertCircle, border: 'border-yellow-500/30', iconBg: 'bg-yellow-50 dark:bg-yellow-900/20', iconColor: 'text-yellow-700 dark:text-yellow-400', title: 'Invitación Expirada', msg: 'Esta invitación ha expirado. Solicita una nueva al dueño de la cadena.' },
      error: { icon: AlertCircle, border: 'border-red-500/30', iconBg: 'bg-red-50 dark:bg-red-900/20', iconColor: 'text-red-700 dark:text-red-400', title: 'Error de Conexión', msg: 'No pudimos verificar la invitación. Revisa tu conexión e intenta de nuevo.' },
    }[status];
    const Icon = cfg.icon;
    return (
      <div className="min-h-screen bg-surface-app flex items-center justify-center p-4">
        <div className={`max-w-md w-full bg-surface-1 rounded-2xl p-8 border ${cfg.border} text-center`}>
          <div className={`w-20 h-20 rounded-full ${cfg.iconBg} flex items-center justify-center mx-auto mb-6`}>
            <Icon size={40} className={cfg.iconColor} />
          </div>
          <h1 className="text-2xl font-bold text-content-primary mb-3">{cfg.title}</h1>
          <p className="text-content-muted mb-6">{cfg.msg}</p>
          <button
            onClick={() => (status === 'error' ? window.location.reload() : navigate('/login'))}
            className="btn-kumia-primary w-full"
          >
            {status === 'error' ? 'Reintentar' : 'Ir al Inicio de Sesión'}
          </button>
        </div>
      </div>
    );
  }

  if (status === 'accepted') {
    return (
      <div className="min-h-screen bg-surface-app flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-surface-1 rounded-2xl p-8 border border-green-500/30 text-center">
          <div className="w-20 h-20 rounded-full bg-green-50 dark:bg-green-900/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle size={40} className="text-kumia-green" />
          </div>
          <h1 className="text-2xl font-bold text-content-primary mb-3">¡Bienvenido a la cadena!</h1>
          <p className="text-content-muted mb-6">
            Ya gestionas <span className="text-kumia-green font-semibold">{invitation?.brandName || 'la cadena'}</span>. Te llevamos a tus sedes...
          </p>
          <div className="flex items-center justify-center gap-2 text-content-subtle">
            <Loader2 size={16} className="animate-spin" />
            <span>Redirigiendo...</span>
          </div>
        </div>
      </div>
    );
  }

  // --- status === 'valid' ---
  const Header = () => (
    <div className="text-center mb-8">
      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-kumia-green to-green-600 flex items-center justify-center mx-auto mb-4">
        <Building2 size={32} className="text-content-primary" />
      </div>
      <h1 className="text-3xl font-bold text-content-primary mb-2">Gestión de Cadena</h1>
      <p className="text-content-muted">
        {invitation?.invitedByName} te invita a gestionar <span className="text-kumia-green font-semibold">{invitation?.brandName}</span> como
        <span className="text-purple-700 dark:text-purple-400 font-semibold"> gerente</span>
        {invitation?.scopeLabel && <> con acceso a <span className="text-content-primary font-medium">{invitation.scopeLabel}</span></>}.
      </p>
    </div>
  );

  // Caso 1: usuario autenticado
  if (user) {
    return (
      <div className="min-h-screen bg-surface-app flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <Header />
          <div className="bg-surface-1 rounded-2xl p-6 border border-line space-y-6">
            <div className="flex items-center gap-4 p-4 bg-surface-base rounded-xl border border-line">
              <div className="w-12 h-12 rounded-full bg-kumia-green/20 flex items-center justify-center flex-shrink-0">
                <User size={24} className="text-kumia-green" />
              </div>
              <div className="min-w-0">
                <p className="text-content-primary font-medium truncate">{user.displayName || 'Usuario'}</p>
                <p className="text-content-muted text-sm truncate">{user.email}</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-500/30 rounded-xl">
              <ShieldCheck size={20} className="text-blue-700 dark:text-blue-400 flex-shrink-0 mt-0.5" />
              <p className="text-blue-700 dark:text-blue-300 text-sm">
                Tu cuenta quedará vinculada a esta cadena como gerente.
              </p>
            </div>
            <button onClick={handleAccept} disabled={isProcessing} className="btn-kumia-primary w-full flex items-center justify-center gap-2">
              {isProcessing ? <><Loader2 size={18} className="animate-spin" />Procesando...</> : <><CheckCircle size={18} />Aceptar Invitación</>}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Caso 2: no autenticado + cuenta existe → login
  if (invitation?.accountExists) {
    return (
      <div className="min-h-screen bg-surface-app flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <Header />
          <div className="bg-surface-1 rounded-2xl p-6 border border-line space-y-6">
            <div className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-500/30 rounded-xl">
              <Mail size={20} className="text-blue-700 dark:text-blue-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-blue-700 dark:text-blue-300 text-sm font-medium mb-1">Ya tienes una cuenta en KumIA</p>
                <p className="text-blue-700 dark:text-blue-300/80 text-sm">
                  El email <span className="text-content-primary font-medium">{maskEmail(invitation.email)}</span> ya está registrado. Inicia sesión para aceptar la invitación.
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate('/login', { state: { returnTo: `/accept-org-invitation/${token}` } })}
              className="btn-kumia-primary w-full flex items-center justify-center gap-2"
            >
              <LogIn size={18} />
              Iniciar Sesión para Aceptar
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Caso 3: usuario nuevo → crear cuenta
  return (
    <div className="min-h-screen bg-surface-app flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <Header />
        <div className="bg-surface-1 rounded-2xl p-6 border border-line">
          <form onSubmit={handleAccept} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-content-secondary mb-2">Tu nombre completo</label>
              <div className="relative">
                <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-content-muted" />
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                  placeholder="Juan Pérez"
                  className={`w-full bg-surface-base text-content-primary pl-10 pr-4 py-3 rounded-xl border ${errors.name ? 'border-red-500' : 'border-line'} focus:border-kumia-green focus:outline-none focus:ring-1 focus:ring-kumia-green/50`}
                />
              </div>
              {errors.name && <p className="text-red-700 dark:text-red-400 text-xs mt-1">{errors.name}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-content-secondary mb-2">Contraseña</label>
              <div className="relative">
                <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-content-muted" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData((p) => ({ ...p, password: e.target.value }))}
                  placeholder="Mínimo 6 caracteres"
                  className={`w-full bg-surface-base text-content-primary pl-10 pr-12 py-3 rounded-xl border ${errors.password ? 'border-red-500' : 'border-line'} focus:border-kumia-green focus:outline-none focus:ring-1 focus:ring-kumia-green/50`}
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-content-muted hover:text-content-primary">
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {errors.password && <p className="text-red-700 dark:text-red-400 text-xs mt-1">{errors.password}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-content-secondary mb-2">Confirmar contraseña</label>
              <div className="relative">
                <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-content-muted" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData((p) => ({ ...p, confirmPassword: e.target.value }))}
                  placeholder="Repite tu contraseña"
                  className={`w-full bg-surface-base text-content-primary pl-10 pr-4 py-3 rounded-xl border ${errors.confirmPassword ? 'border-red-500' : 'border-line'} focus:border-kumia-green focus:outline-none focus:ring-1 focus:ring-kumia-green/50`}
                />
              </div>
              {errors.confirmPassword && <p className="text-red-700 dark:text-red-400 text-xs mt-1">{errors.confirmPassword}</p>}
            </div>
            <div className="flex items-start gap-3 p-4 bg-surface-base border border-line rounded-xl">
              <Mail size={18} className="text-content-muted flex-shrink-0 mt-0.5" />
              <p className="text-content-muted text-sm">
                Se creará una cuenta con el email <span className="text-content-primary font-medium">{maskEmail(invitation?.email)}</span>.
              </p>
            </div>
            <button type="submit" disabled={isProcessing} className="btn-kumia-primary w-full flex items-center justify-center gap-2">
              {isProcessing ? <><Loader2 size={18} className="animate-spin" />Creando cuenta...</> : <><Building2 size={18} />Crear Cuenta y Gestionar</>}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default OrgAcceptInvitation;
